import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import {
  ref,
  computed,
  onMounted,
  onActivated,
  onDeactivated,
  inject,
} from "vue";
import router from "@/router";
import { useUserStore } from "@/stores/userStore";
import { useLineStore } from "@/stores/lineStore";
import { ILineInfoData } from "@/domain/entities/lineInfo";
import Page from "@/components/shared/Page.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import LineInfoRow from "@/components/line/LineInfoRow.vue";
import SearchInput from "@/components/shared/inputs/SearchInput.vue";
import {
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  RouterName,
  ShowErrorModal,
} from "@/types";
import EmptyContent from "@/components/shared/EmptyContent.vue";
import Tabs from "@/components/shared/Tabs.vue";


const __default__ = {
  name: "LineSearch",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

_useCssVars(_ctx => ({
  "e0687a26-lineDistrictsHeight": (lineDistrictsHeight.value)
}))

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;

const userStore = useUserStore();
const lineStore = useLineStore();
const lineInfos = lineStore.lineInfos;
const lineTypes = ref<string[]>([]);
const currentLineType = ref("");
const districts = ref<string[]>([]);
const currentDistricts = ref<string[]>([]);

const searchInput = ref<HTMLInputElement>();
const searchKeyword = ref("");

const shouldShowLineTypes = ref(false); // 노선 타입(출근/퇴근/셔틀) 화면 노출 여부
const shouldShowDistricts = ref(false); // 권역 화면 노출 여부
const lineDistrictsHeight = computed(() =>
  shouldShowDistricts.value ? "44px" : "0px"
);

const getTitle = computed(() => (shouldShowLineTypes.value ? "" : "노선검색"));

const tabsComponent = ref<InstanceType<typeof Tabs>>();
const currentSegmentIndex = ref(0);
let touchStartX = 0;
let touchEndX = 0;
const onTouchStart = (event: TouchEvent) => {
  touchStartX = event.changedTouches[0].screenX;
};
const onTouchEnd = (event: TouchEvent) => {
  touchEndX = event.changedTouches[0].screenX;
  if (Math.abs(touchEndX - touchStartX) < 100) {
    return;
  }
  const changedSegmentIndex =
    currentSegmentIndex.value + (touchEndX < touchStartX ? 1 : -1);
  if (
    changedSegmentIndex < 0 ||
    changedSegmentIndex > lineTypes.value.length - 1
  ) {
    return;
  }
  currentSegmentIndex.value = changedSegmentIndex;
  tabsComponent.value?.changeTabIndex(changedSegmentIndex);
};

const getLinesCount = (district: string) => {
  return lineInfos.filter((lineInfo) => {
    if (lineInfo.lineTypeName !== currentLineType.value) return false;
    return (
      lineInfo.district.includes(district) &&
      (lineInfo.lineNameDisplayText.includes(searchKeyword.value) ||
        lineInfo.lineNameDisplayText
          .toUpperCase()
          .includes(searchKeyword.value.toUpperCase()) ||
        lineInfo.turns.some((turn) => {
          return turn.busStops.some(
            (busStop) =>
              busStop.name.includes(searchKeyword.value) ||
              busStop.name
                .toUpperCase()
                .includes(searchKeyword.value.toUpperCase())
          );
        }))
    );
  }).length;
};

const filteredLineInfos = computed(() => {
  return lineInfos.filter((lineInfo) => {
    if (lineInfo.lineTypeName !== currentLineType.value) return false;
    if (
      currentDistricts.value.length &&
      !currentDistricts.value.includes(lineInfo.district)
    )
      return false;
    if (lineInfo.lineNameDisplayText.includes(searchKeyword.value)) return true;
    if (
      lineInfo.lineNameDisplayText
        .toUpperCase()
        .includes(searchKeyword.value.toUpperCase())
    )
      return true;

    return lineInfo.turns.some((turn) => {
      return turn.busStops.some(
        (busStop) =>
          busStop.name.includes(searchKeyword.value) ||
          busStop.name.toUpperCase().includes(searchKeyword.value.toUpperCase())
      );
    });
  });
});

const filteredDistricts = computed(() => {
  return districts.value.filter((district) => getLinesCount(district) > 0);
});

const onChangedSegmentIndex = (index: number) => {
  currentSegmentIndex.value = index;
  currentLineType.value = lineTypes.value[index];
  searchKeyword.value = "";
  currentDistricts.value = [];
};

const onChangeSearchKeyword = () => {
  currentDistricts.value = [];
};

const goToLineInfoDetail = (lineInfo: ILineInfoData) => {
  searchInput.value?.blur();
  userStore.updateSelectedLineNum(lineInfo.lineNum);
  router.push({
    name: RouterName.ReviewLineInfoDetail,
    params: { lineId: lineInfo.id },
  });
};

const updateLineTypeAndDistricts = async () => {
  const lineTypesResult = await lineStore.getLineTypes();
  const lineTypesData = lineTypesResult.data ?? [];
  shouldShowLineTypes.value = lineTypesData.length > 0;
  lineTypes.value = lineTypesData.map((lineType) => lineType.lineTypeName);
  currentLineType.value = lineTypes.value[0];

  const districtsResult = await lineStore.getLineDistricts();
  const districtsData = districtsResult.data ?? [];
  shouldShowDistricts.value = districtsData.length > 1;
  districts.value = districtsData.map((district) => district.district);

  if (!lineTypesResult.isSuccess || !districtsResult.isSuccess) {
    const title = !lineTypesResult.isSuccess
      ? "노선타입 오류"
      : "노선권역 오류";
    const message =
      lineTypesResult.errorMessage || districtsResult.errorMessage;
    const code = lineTypesResult.resultCode || districtsResult.resultCode;
    const time = lineTypesResult.errorTime || districtsResult.errorTime;
    showErrorModal(title, message, code, time);
  }
};

const onClickDistrict = (district: string) => {
  if (!district.length) {
    currentDistricts.value = [];
    return;
  }
  if (currentDistricts.value.includes(district)) {
    currentDistricts.value = currentDistricts.value.filter(
      (currentDistrict) => currentDistrict !== district
    );
    return;
  }
  currentDistricts.value.push(district);
};

onMounted(() => {
  updateLineTypeAndDistricts();

  setTimeout(() => {
    searchInput.value?.focus();
  }, 200);
});

onActivated(() => {
  setTimeout(() => {
    searchInput.value?.focus();
  }, 200);
});

onDeactivated(() => {
  setTimeout(() => {
    searchInput.value?.blur();
  }, 200);
});

const __returned__ = { showErrorModal, userStore, lineStore, lineInfos, lineTypes, currentLineType, districts, currentDistricts, searchInput, searchKeyword, shouldShowLineTypes, shouldShowDistricts, lineDistrictsHeight, getTitle, tabsComponent, currentSegmentIndex, get touchStartX() { return touchStartX }, set touchStartX(v) { touchStartX = v }, get touchEndX() { return touchEndX }, set touchEndX(v) { touchEndX = v }, onTouchStart, onTouchEnd, getLinesCount, filteredLineInfos, filteredDistricts, onChangedSegmentIndex, onChangeSearchKeyword, goToLineInfoDetail, updateLineTypeAndDistricts, onClickDistrict, Page, NavigationBar, LineInfoRow, SearchInput, EmptyContent, Tabs }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})